/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call -- JSON inspector traverses any tree */
import { useState } from 'react'
import { IconButton } from '../components/icon-button'
import { ChevronRightIcon, ChevronDownIcon } from '../components/icon'

interface JsonViewerProps {
  data: a
  expandedUntil?: number
  className?: string
}

export function JsonViewer({
  data,
  expandedUntil = 1,
  className,
}: JsonViewerProps) {
  return (
    <div className={className}>
      <RootNode data={data} expandedUntil={expandedUntil} />
    </div>
  )
}

interface JsonNodeProps {
  name: string
  data: a
  expandedUntil?: number
  level?: number
}

function JsonNode({ name, data, expandedUntil = 1, level = 0 }: JsonNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level < expandedUntil)

  const dataType = getDataType(data)
  const isExpandable = dataType === 'object' || dataType === 'array'

  const renderValue = () => {
    switch (dataType) {
      case 'string':
        return (
          <span className="text-sm text-green-600 dark:text-green-400">
            {'"'}
            {data}
            {'"'}
          </span>
        )
      case 'number':
        return (
          <span className="text-sm text-blue-600 dark:text-blue-400">
            {data}
          </span>
        )
      case 'boolean':
        return (
          <span className="text-sm text-purple-600 dark:text-purple-400">
            {data.toString()}
          </span>
        )
      case 'null':
        return (
          <span className="text-sm text-red-600 italic dark:text-red-400">
            null
          </span>
        )
      case 'undefined':
        return (
          <span className="text-sm text-red-600 italic dark:text-red-400">
            undefined
          </span>
        )
      case 'object':
        return (
          <span className="text-sm text-gray-500">{`{ ${Object.keys(data).length} }`}</span>
        )
      case 'array':
        return (
          <span className="text-sm text-gray-500">{`[ ${data.length} ]`}</span>
        )
      default:
        return <span>{String(data)}</span>
    }
  }

  const renderObjectContent = () => {
    if (dataType === 'object') {
      const entries = Object.entries(data)
      return (
        <div className="border-border/50 border-l pl-4">
          {entries.length === 0 ? (
            <div className="text-gray-500 italic">{'{ }'}</div>
          ) : (
            entries.map(([key, value]) => (
              <JsonNode
                key={key}
                name={key}
                data={value}
                expandedUntil={expandedUntil}
                level={level + 1}
              />
            ))
          )}
        </div>
      )
    }

    if (dataType === 'array') {
      return (
        <div className="border-border/50 border-l pl-4">
          {data.length === 0 ? (
            <div className="text-gray-500 italic">[ ]</div>
          ) : (
            data.map((item: a, index: number) => (
              <JsonNode
                // oxlint-disable-next-line react/no-array-index-key -- no other unique identifier
                key={index}
                name={index.toString()}
                data={item}
                expandedUntil={expandedUntil}
                level={level + 1}
              />
            ))
          )}
        </div>
      )
    }

    return null
  }

  if (isExpandable) {
    return (
      <div>
        <div className="flex items-start">
          <IconButton
            className="h-5 w-5 p-0"
            size="sm"
            onClick={() => {
              setIsExpanded(!isExpanded)
            }}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            Toggle
          </IconButton>
          <div className="flex items-center">
            <span className="mr-2 font-mono text-sm font-medium">
              {dataType === 'array' ? `${name}[${data.length}]` : name}:
            </span>
            <span className="text-on-secondary text-sm">
              {dataType === 'object'
                ? `{ ${Object.keys(data).length} }`
                : `[ ${data.length} ]`}
            </span>
          </div>
        </div>
        {isExpanded && renderObjectContent()}
      </div>
    )
  }

  return (
    <div className="flex items-center">
      <div className="w-5" />
      <span className="mr-2 font-mono text-sm font-medium">{name}:</span>
      {renderValue()}
    </div>
  )
}

function getDataType(data: a): string {
  if (data === null) return 'null'
  if (data === undefined) return 'undefined'
  if (Array.isArray(data)) return 'array'
  return typeof data
}

function RootNode({ data, expandedUntil }: { data: a; expandedUntil: number }) {
  const dataType = getDataType(data)

  if (dataType === 'object') {
    return (
      <div>
        {Object.entries(data).length === 0 ? (
          <div className="text-gray-500 italic">{'{ }'}</div>
        ) : (
          Object.entries(data).map(([key, value]) => (
            <JsonNode
              key={key}
              name={key}
              data={value}
              expandedUntil={expandedUntil}
              level={0}
            />
          ))
        )}
      </div>
    )
  }

  if (dataType === 'array') {
    return (
      <div>
        {data.length === 0 ? (
          <div className="text-gray-500 italic">[ ]</div>
        ) : (
          data.map((item: a, index: number) => (
            <JsonNode
              // oxlint-disable-next-line react/no-array-index-key -- no other unique identifier
              key={index}
              name={index.toString()}
              data={item}
              expandedUntil={expandedUntil}
              level={0}
            />
          ))
        )}
      </div>
    )
  }

  // For primitive values at root
  return (
    <JsonNode name="" data={data} expandedUntil={expandedUntil} level={0} />
  )
}

// oxlint-disable-next-line typescript/no-explicit-any -- aaa
type a = any
